/**
 * NotesEmptyState Component
 *
 * Empty state for when no notes are found,
 * following the PatientEmptyState design pattern.
 */

import React from 'react';
import {
  Mic as MicIcon,
  NoteAdd as NoteAddIcon,
  SearchOff as SearchOffIcon,
} from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { useRecordingSession } from '../../../hooks/useRecordingSession';

export interface NotesEmptyStateProps {
  isSearchActive: boolean;
  searchTerm: string;
  hasGeneratedFirstNote: boolean;
  onClearSearch: () => void;
  onClearFilters?: () => void;
}

export const NotesEmptyState: React.FC<NotesEmptyStateProps> = ({
  isSearchActive,
  searchTerm,
  hasGeneratedFirstNote,
  onClearSearch,
  onClearFilters,
}) => {
  const theme = useTheme();
  const {
    startRecording,
    canStartRecording,
    isLoadingAppointments,
    requiresScheduledAppointmentForNoteCreation,
  } = useRecordingSession();
  const recordDisabled =
    requiresScheduledAppointmentForNoteCreation && (isLoadingAppointments || !canStartRecording);

  // Search with no results
  if (isSearchActive) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 8,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.grey[500], 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <SearchOffIcon sx={{ fontSize: 28, color: theme.palette.grey[400] }} />
        </Box>

        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}
        >
          No results found
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, maxWidth: 320, mb: 3 }}
        >
          No notes matching &ldquo;{searchTerm}&rdquo;
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClearSearch}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Clear Search
          </Button>
          {onClearFilters && (
            <Button
              variant="text"
              size="small"
              onClick={onClearFilters}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              Reset Filters
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // No notes yet - first time user
  if (!hasGeneratedFirstNote) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 8,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <MicIcon sx={{ fontSize: 36, color: theme.palette.primary.main }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 1 }}>
          Create your first note
        </Typography>

        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary, maxWidth: 400, mb: 4 }}
        >
          Record a session with a patient and JotPsych will automatically generate comprehensive
          clinical documentation.
        </Typography>

        <Button
          variant="contained"
          size="large"
          startIcon={<MicIcon />}
          disabled={recordDisabled}
          onClick={() => {
            // Keep this guard for keyboard/programmatic activation while appointment state loads.
            if (recordDisabled) return;
            startRecording();
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 2,
          }}
        >
          Start Recording
        </Button>
      </Box>
    );
  }

  // Has notes but current filter returns empty
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 3,
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.grey[500], 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <NoteAddIcon sx={{ fontSize: 28, color: theme.palette.grey[400] }} />
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
        No notes in this category
      </Typography>

      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, maxWidth: 320, mb: 3 }}
      >
        Try changing your filters or create a new note to get started.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {onClearFilters && (
          <Button
            variant="outlined"
            size="small"
            onClick={onClearFilters}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            View All Notes
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<MicIcon />}
          disabled={recordDisabled}
          onClick={() => {
            // Keep this guard for keyboard/programmatic activation while appointment state loads.
            if (recordDisabled) return;
            startRecording();
          }}
          sx={{ textTransform: 'none', fontWeight: 500 }}
        >
          New Recording
        </Button>
      </Box>
    </Box>
  );
};
